import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { requireAuthenticatedContext, shouldSkipAuth } from '@/lib/auth';

export type ElementType = 'fixture' | 'control' | 'product';

export interface ElementSizeDefault {
  id: string;
  clerk_org_id: string;
  element_type: ElementType;
  category: string;
  subcategory?: string | null;
  default_scale: number;
  created_at: string;
  updated_at: string;
}

/**
 * Get default scale for element category
 * Returns null if no default is set
 */
export async function getDefaultScale(
  elementType: ElementType,
  category: string,
  subcategory?: string,
): Promise<number | null> {
  const supabase = await getAuthenticatedSupabaseClient();

  // Try exact match first (with subcategory)
  if (subcategory) {
    const { data, error } = await supabase
      .from('element_size_defaults')
      .select('default_scale')
      .eq('element_type', elementType)
      .eq('category', category)
      .eq('subcategory', subcategory)
      .maybeSingle();

    if (error) {
      logger.error('[getDefaultScale] Error fetching default:', error);
    }

    if (data) return data.default_scale;
  }

  // Fallback to category-level default (subcategory is empty string in DB)
  const { data, error } = await supabase
    .from('element_size_defaults')
    .select('default_scale')
    .eq('element_type', elementType)
    .eq('category', category)
    .eq('subcategory', '')
    .maybeSingle();

  if (error) {
    logger.error('[getDefaultScale] Error fetching default:', error);
  }

  return data?.default_scale ?? null;
}

/**
 * Set default scale for element category
 * Uses upsert to create or update the default
 */
export async function setDefaultScale(
  elementType: ElementType,
  category: string,
  defaultScale: number,
  subcategory?: string,
): Promise<ElementSizeDefault> {
  // Validation
  if (defaultScale < 0.2 || defaultScale > 5.0) {
    throw new Error('Scale must be between 0.2 and 5.0');
  }

  if (!category || category.trim() === '') {
    throw new Error('Category cannot be empty');
  }

  const supabase = await getAuthenticatedSupabaseClient();

  // Normalize subcategory: use empty string instead of null to match DB schema
  const normalizedSubcategory = subcategory || '';

  // Get org ID - required for both local and production
  let clerkOrgId: string;

  if (shouldSkipAuth()) {
    // Local dev with SKIP_AUTH: use default org ID
    clerkOrgId = 'org_local_development';
    logger.info('[setDefaultScale] Using local development org ID');
  } else {
    // Staging/Production: Get org ID from Clerk session (matches app pattern)
    const context = await requireAuthenticatedContext();

    if (!context || !context.organizationId) {
      logger.error(
        '[setDefaultScale] No organization ID in Clerk session. User might not be in an organization.',
      );
      throw new Error('You must be part of an organization to save default element sizes');
    }

    clerkOrgId = context.organizationId;
    logger.info('[setDefaultScale] Retrieved org ID from Clerk context:', clerkOrgId);
  }

  // Build upsert payload - manually provide clerk_org_id (matches projects table pattern)
  const upsertPayload = {
    clerk_org_id: clerkOrgId,
    element_type: elementType,
    category,
    subcategory: normalizedSubcategory,
    default_scale: defaultScale,
    updated_at: new Date().toISOString(),
  };

  logger.info('[setDefaultScale] Upserting default scale:', upsertPayload);

  const { data, error } = await supabase
    .from('element_size_defaults')
    .upsert(upsertPayload, {
      onConflict: 'clerk_org_id,element_type,category,subcategory',
    })
    .select()
    .single();

  if (error) {
    logger.error('[setDefaultScale] Error setting default scale:', error);
    throw error;
  }

  logger.info('[setDefaultScale] Successfully saved default scale:', data);
  return data as ElementSizeDefault;
}

/**
 * Get all defaults for the current organization
 */
export async function getAllDefaults(): Promise<ElementSizeDefault[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('element_size_defaults')
    .select('*')
    .order('element_type', { ascending: true })
    .order('category', { ascending: true });

  if (error) {
    logger.error('Error fetching size defaults:', error);
    throw error;
  }

  return (data || []) as ElementSizeDefault[];
}

/**
 * Delete a default
 */
export async function deleteDefault(id: string): Promise<void> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { error } = await supabase.from('element_size_defaults').delete().eq('id', id);

  if (error) {
    logger.error('Error deleting size default:', error);
    throw error;
  }
}

/**
 * Reset all defaults to 1.0
 */
export async function resetAllDefaults(): Promise<void> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { error } = await supabase
    .from('element_size_defaults')
    .delete()
    .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all (condition always true)

  if (error) {
    logger.error('Error resetting defaults:', error);
    throw error;
  }
}
